/* THE PURSUIT FIELD POLICY, ASKED ONCE PER LINE PER PAGE LOAD.
 *
 * ⚠️ CACHED PER LINE, NOT GLOBALLY — and that is the one difference from
 * `useFieldPolicy.js`, which shares a single promise because there is only one
 * championship object.
 *
 * There are eight lines and they do NOT agree. Measured live 2026-09-14:
 * corporate/government/greek/military/police/fire are `companies` with the same
 * 8 + 5 fields, cheer adds `sr_org_id` to the privileged tier, and RFP is
 * `deals` with a completely different set (`sr_rfp_status`, `closedate`,
 * `amount`, …) and a DIFFERENT `never` list (`dealstage`, `pipeline`). One
 * shared promise would hand the RFP drawer the corporate policy and draw five
 * company fields over a deal.
 *
 * Within a line the policy is still a statement about the CALLER, not about a
 * record — byte identical for every row — so one in-flight promise per line is
 * shared by every control on the page. Two controls in one drawer resolving
 * different policies would draw one field editable and an identical one not.
 */
import { useEffect, useState } from 'react'
import {createSessionCache} from '../../../lib/sessionGeneration.js'
import {useSessionGeneration} from '../../../lib/useSessionGeneration.js'
import { loadPursuitFieldPolicy } from './pursuitEditRun.js'
import { readPursuitPolicy } from './pursuitEdit.js'

const shared = createSessionCache()

/** Reset between sessions. Sign-out changes the caller, and the caller IS the
 *  policy — a cached exec policy surviving into a rep's session would draw
 *  privileged inputs that 403. */
export function resetPursuitFieldPolicy() {
  shared.clear()
}

export function sharedPursuitFieldPolicy(line) {
  const key = String(line || '')
  if (!key) return Promise.resolve(readPursuitPolicy(null))
  return shared.load(key, () => loadPursuitFieldPolicy(key)
    .then((res) => readPursuitPolicy(res))
    .catch(() => readPursuitPolicy(null)))
}

export function usePursuitFieldPolicy(line) {
  const [policy, setPolicy] = useState(null)
  const generation = useSessionGeneration()

  useEffect(() => {
    if (!line) { setPolicy(null); return undefined }
    let alive = true
    setPolicy(null)
    sharedPursuitFieldPolicy(line).then((p) => { if (alive) setPolicy({value: p, generation}) }).catch(() => { if (alive) setPolicy(null) })
    return () => { alive = false }
  }, [line, generation])

  return policy?.generation === generation ? policy.value : null
}
